import React, { FC, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { PriceRequestCounterpart } from 'models/price-request';

type CounterpartAutocompleteProps = {
    label: string;
    counterparts: PriceRequestCounterpart[];
    /** The chosen counterpart's id; '' for none. */
    value: string;
    onChange: (counterpartId: string) => void;
    disabled?: boolean;
    size?: 'small' | 'medium';
    /** Shown in an empty field, e.g. "All labs" in the log filter. */
    placeholder?: string;
    dataTest: string;
};

const nameOf = (counterpart: PriceRequestCounterpart) => counterpart.name || counterpart._id;

/**
 * DD-3697: pick the lab (for a chain) or the chain (for a lab). A chain can have dozens of
 * labs, so it is a type-to-filter list sorted by name rather than a plain dropdown.
 */
const CounterpartAutocomplete: FC<CounterpartAutocompleteProps> = ({
    label,
    counterparts,
    value,
    onChange,
    disabled,
    size = 'medium',
    placeholder,
    dataTest,
}) => {
    const options = useMemo(
        () => [...counterparts].sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'sv')),
        [counterparts],
    );
    const selected = options.find((counterpart) => counterpart._id === value) ?? null;

    return (
        <Autocomplete
            options={options}
            value={selected}
            onChange={(_event, counterpart) => onChange(counterpart?._id ?? '')}
            getOptionLabel={nameOf}
            isOptionEqualToValue={(option, chosen) => option._id === chosen._id}
            disabled={disabled}
            size={size}
            fullWidth
            autoHighlight
            // Rendered in place, so the list lines up under the field inside dialogs too.
            disablePortal
            renderOption={(props, counterpart) => (
                <li {...props} key={counterpart._id}>
                    {nameOf(counterpart)}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    // Keep the label up only when there is a placeholder to show beneath it.
                    InputLabelProps={placeholder ? { ...params.InputLabelProps, shrink: true } : params.InputLabelProps}
                />
            )}
            data-test={dataTest}
        />
    );
};

export default CounterpartAutocomplete;
